import type { ProviderInteractionMode, RuntimeMode } from "@cmmd-forge/contracts";
import { memo } from "react";
import {
  BotIcon,
  type LucideIcon,
  LockIcon,
  LockOpenIcon,
  PenLineIcon,
  PencilRulerIcon,
} from "lucide-react";
import {
  COMPOSER_CONTROL_CLASS,
  ComposerControl,
  ComposerControlChevron,
  ComposerControlIcon,
  ComposerControlSeparator,
} from "./ComposerControl";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { useSettings } from "~/hooks/useSettings";
import { cn } from "~/lib/utils";
import { composerFloatingLayerProps } from "./composerEventScope";

const runtimeModeConfig: Record<
  RuntimeMode,
  { label: string; description: string; icon: LucideIcon }
> = {
  "approval-required": {
    label: "Supervised",
    description: "Ask before commands and file changes.",
    icon: LockIcon,
  },
  "auto-accept-edits": {
    label: "Auto-accept edits",
    description: "Auto-approve edits, ask before other actions.",
    icon: PenLineIcon,
  },
  "full-access": {
    label: "Full access",
    description: "Allow commands and edits without prompts.",
    icon: LockOpenIcon,
  },
};

const runtimeModeOptions = Object.keys(runtimeModeConfig) as RuntimeMode[];

const BuildPlanModeToggleButton = memo(function BuildPlanModeToggleButton(props: {
  readonly interactionMode: ProviderInteractionMode;
  readonly showInteractionLabelOnMobile?: boolean | undefined;
  readonly showSeparators: boolean;
  readonly onToggleInteractionMode: () => void;
}) {
  const isPlanMode = props.interactionMode === "plan";
  // The name says what pressing it does, which is t3code's wording. The
  // visible word is the mode you are in, so on its own it would read as a
  // label rather than an action.
  const actionName = isPlanMode
    ? "Plan mode - click to return to normal build mode"
    : "Default mode - click to enter plan mode";
  return (
    <>
      {props.showSeparators ? <ComposerControlSeparator /> : null}

      <ComposerControl
        className={cn(
          "shrink-0",
          // Plan mode is a state the row has to advertise, not a preference
          // buried in a tooltip: the agent stops making changes in it.
          isPlanMode ? "bg-accent text-accent-foreground hover:bg-accent/80" : undefined,
        )}
        type="button"
        onClick={props.onToggleInteractionMode}
        aria-label={actionName}
        title={actionName}
      >
        <ComposerControlIcon icon={isPlanMode ? PencilRulerIcon : BotIcon} />
        <span className={props.showInteractionLabelOnMobile ? undefined : "sr-only md:not-sr-only"}>
          {isPlanMode ? "Plan" : "Build"}
        </span>
      </ComposerControl>
    </>
  );
});

export const ComposerFooterModeControls = memo(function ComposerFooterModeControls(props: {
  readonly interactionMode: ProviderInteractionMode;
  readonly runtimeMode: RuntimeMode;
  readonly showInteractionLabelOnMobile?: boolean;
  readonly showSeparators?: boolean;
  readonly onToggleInteractionMode: () => void;
  readonly onRuntimeModeChange: (mode: RuntimeMode) => void;
}) {
  const runtimeModeOption = runtimeModeConfig[props.runtimeMode];
  const RuntimeModeIcon = runtimeModeOption.icon;
  const showSeparators = props.showSeparators ?? true;
  const showInteractionModeToggle = useSettings((s) => s.showInteractionModeToggle);

  // Runtime mode leads and Build/Plan follows, which is t3code's order in this
  // cluster. The footer reads model, effort, runtime mode, Build/Plan from the
  // left, so the two controls that decide what the agent may do sit together at
  // the end rather than being split by the model and effort pickers. The plan
  // sidebar toggle is not part of it: t3code has no such control, so Forge's
  // lives in the controls strip's overflow menu.
  return (
    <>
      {showSeparators ? <ComposerControlSeparator /> : null}

      <Select
        value={props.runtimeMode}
        onValueChange={(value) => props.onRuntimeModeChange(value!)}
      >
        <SelectTrigger
          variant="ghost"
          size="sm"
          className={cn(COMPOSER_CONTROL_CLASS, "shrink-0")}
          icon={<ComposerControlChevron />}
          aria-label="Runtime mode"
          title={runtimeModeOption.description}
        >
          <ComposerControlIcon icon={RuntimeModeIcon} />
          <SelectValue>
            {/* Visible at every width. Runtime mode is the highest-consequence control in the footer
                ("Full access" runs commands and edits files without asking),
                and `title` never fires on touch — so an icon-only glyph left
                mobile users with no way to read their permission state. */}
            <span>{runtimeModeOption.label}</span>
          </SelectValue>
        </SelectTrigger>
        <SelectPopup alignItemWithTrigger={false} {...composerFloatingLayerProps}>
          {runtimeModeOptions.map((mode) => {
            const option = runtimeModeConfig[mode];
            const OptionIcon = option.icon;
            return (
              <SelectItem key={mode} value={mode} hideIndicator className="min-w-64 py-2">
                <div className="grid min-w-0 gap-0.5">
                  <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                    <OptionIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    {option.label}
                  </span>
                  <span className="text-muted-foreground text-xs leading-4">
                    {option.description}
                  </span>
                </div>
              </SelectItem>
            );
          })}
        </SelectPopup>
      </Select>

      {showInteractionModeToggle ? (
        <BuildPlanModeToggleButton
          interactionMode={props.interactionMode}
          showInteractionLabelOnMobile={props.showInteractionLabelOnMobile}
          showSeparators={showSeparators}
          onToggleInteractionMode={props.onToggleInteractionMode}
        />
      ) : null}
    </>
  );
});
